import React, { useEffect, useMemo } from "react";
import { Card, Nav, Spinner } from "react-bootstrap";
import Entradas from "./Entradas";
import styled from "styled-components";
import {
  DetalleTransporte,
  useObtenerHojaDeTransporteDeHoy,
  QUERY_OBTENER_HOJA_DE_TRANSPORTE_DE_HOY,
  esEntrada,
  modificarHojaDiaria,
  ModificacionesHojaDiaria,
  ModificacionesDetalleTransporteDiario,
} from "../../../services/transporteServicios";
import ServerErrorModal from "../../organisms/ServerErrorModal";
import DetalleModal from "./DetalleModal";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ModificacionesDetalle } from "./Modificaciones";
import SuccessButton from "../../organisms/SuccessButton";
import Salidas from "./Salidas";
import LoadingScreen from "../../organisms/LoadingScreen";
import { buildValueChange } from "../../../utils/ValueChange";

function EntradasSalidas() {
  const queryClient = useQueryClient();

  const [tipoDetalle, setTipoDetalle] = React.useState<"entradas" | "salidas">(
    "entradas"
  );

  const [detalleSeleccionado, setDetalleSeleccionado] = React.useState<
    DetalleTransporte | undefined
  >(undefined);

  const [modificacionesEntradas, setModificacionesEntradas] = React.useState<
    Map<string, ModificacionesDetalle> | undefined
  >(undefined);

  const [modificacionesSalidas, setModificacionesSalidas] = React.useState<
    Map<string, ModificacionesDetalle> | undefined
  >(undefined);

  const {
    data: hojaDeTransporte,
    isLoading: hojaDeTransporteIsLoading,
    error: hojaDeTransporteError,
  } = useObtenerHojaDeTransporteDeHoy();

  const modificacionesMutacion = useMutation(modificarHojaDiaria, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_OBTENER_HOJA_DE_TRANSPORTE_DE_HOY],
      });
    },
  });

  const obtenerOCrearModificacionesEntradas = () => {
    let modEntradas = modificacionesEntradas;
    if (!modEntradas) {
      modEntradas = new Map<string, ModificacionesDetalle>();
    } else {
      modEntradas = new Map(modEntradas);
    }

    return modEntradas;
  };

  const obtenerOCrearModificacionesSalidas = () => {
    let modEntradas = modificacionesSalidas;
    if (!modEntradas) {
      modEntradas = new Map<string, ModificacionesDetalle>();
    } else {
      modEntradas = new Map(modEntradas);
    }

    return modEntradas;
  };

  const obtenerOrdenDetalle = (
    detalle: DetalleTransporte,
    modificaciones: Map<string, ModificacionesDetalle>
  ) => {
    const mods = modificaciones.get(detalle.id);
    if (mods?.orden) {
      return mods!.orden!;
    }
    return detalle.orden;
  };

  const onSubirOrdenDetalle = (
    detalle: DetalleTransporte,
    anterior: DetalleTransporte
  ) => {
    let modDetalle = esEntrada(detalle)
      ? obtenerOCrearModificacionesEntradas()
      : obtenerOCrearModificacionesSalidas();

    const ordenAnterior = obtenerOrdenDetalle(anterior, modDetalle);
    const ordenEntrada = obtenerOrdenDetalle(detalle, modDetalle);

    let modsActualesDet = modDetalle!.get(detalle.id);

    if (modsActualesDet === undefined) {
      modsActualesDet = {
        detalle: detalle,
        orden: ordenAnterior,
      };
    } else {
      modsActualesDet = {
        ...modsActualesDet,
        orden: ordenAnterior,
      };
    }
    modDetalle.set(detalle.id, modsActualesDet!);

    // bajar detalle anterior
    let modsActualesDetAnterior = modDetalle!.get(anterior.id);
    if (modsActualesDetAnterior === undefined) {
      modsActualesDetAnterior = {
        detalle: anterior,
        orden: ordenEntrada,
      };
    } else {
      modsActualesDetAnterior = {
        ...modsActualesDetAnterior,
        orden: ordenEntrada,
      };
    }
    modDetalle.set(anterior.id, modsActualesDetAnterior!);

    if (esEntrada(detalle)) {
      setModificacionesEntradas(modDetalle);
    } else {
      setModificacionesSalidas(modDetalle);
    }
  };

  const onBajarOrdenDetalle = (
    detalle: DetalleTransporte,
    siguiente: DetalleTransporte
  ) => {
    let modDetalle = esEntrada(detalle)
      ? obtenerOCrearModificacionesEntradas()
      : obtenerOCrearModificacionesSalidas();

    const ordenSiguiente = obtenerOrdenDetalle(siguiente, modDetalle);
    const ordenEntrada = obtenerOrdenDetalle(detalle, modDetalle);

    let modsActualesDet = modDetalle!.get(detalle.id);
    if (modsActualesDet === undefined) {
      modsActualesDet = {
        detalle: detalle,
        orden: ordenSiguiente,
      };
    } else {
      modsActualesDet = {
        ...modsActualesDet,
        orden: ordenSiguiente,
      };
    }
    modDetalle.set(detalle.id, modsActualesDet!);

    // subir la siguiente entrada
    let modsActualesDetSiguiente = modDetalle!.get(siguiente.id);
    if (modsActualesDetSiguiente === undefined) {
      modsActualesDetSiguiente = {
        detalle: siguiente,
        orden: ordenEntrada,
      };
    } else {
      modsActualesDetSiguiente = {
        ...modsActualesDetSiguiente,
        orden: ordenEntrada,
      };
    }
    modDetalle.set(siguiente.id, modsActualesDetSiguiente!);
    if (esEntrada(detalle)) {
      setModificacionesEntradas(modDetalle);
    } else {
      setModificacionesSalidas(modDetalle);
    }
  };

  const onChequearDetalle = (
    detalle: DetalleTransporte,
    transportado: boolean
  ) => {
    let modDetalle = esEntrada(detalle)
      ? obtenerOCrearModificacionesEntradas()
      : obtenerOCrearModificacionesSalidas();

    let modsActualesDet = modDetalle!.get(detalle.id);
    if (modsActualesDet === undefined) {
      modsActualesDet = {
        detalle: detalle,
        transportado: transportado,
      };
    } else {
      modsActualesDet = {
        ...modsActualesDet,
        transportado: transportado,
      };
    }
    modDetalle.set(detalle.id, modsActualesDet!);

    if (esEntrada(detalle)) {
      setModificacionesEntradas(modDetalle);
    } else {
      setModificacionesSalidas(modDetalle);
    }
  };

  const onGuardarClick = () => {
    const modsEntradas: ModificacionesDetalleTransporteDiario[] =
      modificacionesEntradas
        ? Array.from(modificacionesEntradas.values()).map((modDetalle) => {
            return {
              id: modDetalle.detalle.id,
              orden:
                modDetalle.orden !== undefined
                  ? buildValueChange(
                      modDetalle.detalle.orden,
                      modDetalle.orden!
                    )
                  : undefined,
              transportado:
                modDetalle.transportado !== undefined
                  ? buildValueChange(
                      modDetalle.detalle.transportado,
                      modDetalle.transportado!
                    )
                  : undefined,
            };
          })
        : [];

    const modsSalidas: ModificacionesDetalleTransporteDiario[] =
      modificacionesSalidas
        ? Array.from(modificacionesSalidas.values()).map((modDetalle) => {
            return {
              id: modDetalle.detalle.id,
              orden:
                modDetalle.orden !== undefined
                  ? buildValueChange(
                      modDetalle.detalle.orden,
                      modDetalle.orden!
                    )
                  : undefined,
              transportado:
                modDetalle.transportado !== undefined
                  ? buildValueChange(
                      modDetalle.detalle.transportado,
                      modDetalle.transportado!
                    )
                  : undefined,
            };
          })
        : [];

    const modificacionesHojaDiaria: ModificacionesHojaDiaria = {
      id: hojaDeTransporte!.id,
      entradas: modsEntradas,
      salidas: modsSalidas,
    };

    modificacionesMutacion.mutate(modificacionesHojaDiaria);
  };

  const onDetalleClick = (detalle: DetalleTransporte | undefined) => {
    setDetalleSeleccionado(detalle);
  };

  useEffect(() => {
    if (modificacionesMutacion.isLoading) {
      setModificacionesEntradas(undefined);
      setModificacionesSalidas(undefined);
    }
  }, [modificacionesMutacion]);

  const DetalleScreen = useMemo(() => {
    if (detalleSeleccionado) {
      return (
        <DetalleModal
          detalle={detalleSeleccionado}
          onCloseClick={() => onDetalleClick(undefined)}
        />
      );
    } else {
      return <></>;
    }
  }, [detalleSeleccionado]);

  const error = hojaDeTransporteError || modificacionesMutacion.error;
  const mutacionIsLoading = modificacionesMutacion.isLoading;

  if (error) {
    return <ServerErrorModal />;
  }

  if (hojaDeTransporteIsLoading) {
    return <LoadingScreen />;
  }

  return (
    <>
      <Card>
        <StyledCardHeader className="row justify-content-between">
          <div className="d-flex col-8 justify-content-end m-0 p-0">
            <div className="row align-self-end m-0 p-0 text-center">
              <h5>Hoja diaria</h5>
            </div>
          </div>
          <div className="d-flex col-4 justify-content-end">
            <div className="align-self-end">
              <SuccessButton
                variant="success"
                onClick={onGuardarClick}
                size="sm"
              >
                <>
                  {mutacionIsLoading && (
                    <Spinner size="sm" animation="border" />
                  )}
                  {!mutacionIsLoading && "Guardar"}
                </>
              </SuccessButton>
            </div>
          </div>
        </StyledCardHeader>
        <Card.Body>
          <Nav variant="tabs" defaultActiveKey={"entradas"}>
            <Nav.Item>
              <Nav.Link
                eventKey={"entradas"}
                onClick={() => {
                  setTipoDetalle("entradas");
                }}
              >
                Entradas
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey={"salidas"}
                onClick={() => {
                  setTipoDetalle("salidas");
                }}
              >
                Salidas
              </Nav.Link>
            </Nav.Item>
          </Nav>
          {tipoDetalle === "entradas" && !mutacionIsLoading && (
            <Entradas
              entradas={hojaDeTransporte?.entradas || []}
              onDetalleClick={onDetalleClick}
              onChequearEntrada={onChequearDetalle}
              modificacionesEntradas={modificacionesEntradas}
              onSubirOrden={onSubirOrdenDetalle}
              onBajarOrden={onBajarOrdenDetalle}
            />
          )}
          {tipoDetalle === "salidas" && !mutacionIsLoading && (
            <Salidas
              salidas={hojaDeTransporte?.salidas || []}
              onDetalleClick={onDetalleClick}
              onChequearSalida={onChequearDetalle}
              modificacionesSalidas={modificacionesSalidas}
              onSubirOrden={onSubirOrdenDetalle}
              onBajarOrden={onBajarOrdenDetalle}
            />
          )}
        </Card.Body>
      </Card>
      {DetalleScreen}
    </>
  );
}

export default EntradasSalidas;

const StyledCardHeader = styled(Card.Header)`
  border-color: #eaeaea;
  position: relative;
  background: transparent;
  padding: 1.25rem 1.25rem 5px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 0px;
`;
